import { watch } from 'vue'

import { apiService, type UiSettingResponse } from '@/services/apiService'
import { connectivityService } from '@/services/connectivityService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isTauri } from '@/services/platformService'
import { settingsService, type SyncableUiSetting } from '@/services/settingsService'
import { isUiSettingSyncable } from '@/services/uiSettingsRegistry'
import { useAuthStore } from '@/stores/authStore'

const SYNC_INTERVAL_MS = 60_000
const CHANGE_SYNC_DELAY_MS = 1_000

function parseServerUpdatedAt(value: string): number {
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : Date.now()
}

function toSyncableSetting(setting: UiSettingResponse): SyncableUiSetting {
  return {
    key: setting.key,
    value: setting.value,
    updatedAt: parseServerUpdatedAt(setting.updatedAt),
  }
}

class UiSettingsSyncService {
  private isSetup = false
  private intervalId: ReturnType<typeof setInterval> | null = null
  private changeTimer: ReturnType<typeof setTimeout> | null = null
  private isSyncing = false
  private syncRequested = false

  private hasWindowFocus() {
    if (typeof document === 'undefined') return true
    if (document.visibilityState === 'hidden') return false
    return typeof document.hasFocus !== 'function' || document.hasFocus()
  }

  setup() {
    if (this.isSetup) return
    this.isSetup = true

    const authStore = useAuthStore()

    settingsService.subscribe(({ key, source }) => {
      if (source !== 'local' || !isUiSettingSyncable(key) || !authStore.isAuthenticated) return
      this.scheduleSync(0)
    })

    watch(
      () => authStore.isAuthenticated,
      (isAuthenticated) => {
        if (isAuthenticated) {
          this.scheduleSync(0)
        } else {
          this.clearChangeTimer()
        }
      },
      { immediate: true },
    )

    if (this.intervalId === null) {
      this.intervalId = setInterval(() => {
        this.scheduleSync(0)
      }, SYNC_INTERVAL_MS)
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.hasWindowFocus()) {
        this.scheduleSync(0)
      }
    })

    window.addEventListener('focus', () => {
      this.scheduleSync(0)
    })

    if (!isTauri()) return

    void import('@tauri-apps/api/window')
      .then(({ getCurrentWindow }) =>
        getCurrentWindow().onFocusChanged(({ payload: focused }) => {
          if (focused) {
            this.scheduleSync(0)
          }
        }),
      )
      .catch((error) => {
        logService.warn(
          LOG_CATEGORIES.GENERAL,
          'Failed to initialize Tauri UI settings focus hook',
          {
            error,
          },
        )
      })
  }

  scheduleSync(delayMs = CHANGE_SYNC_DELAY_MS) {
    if (!useAuthStore().isAuthenticated) return
    if (!this.hasWindowFocus()) return

    this.clearChangeTimer()
    this.changeTimer = setTimeout(() => {
      this.changeTimer = null
      void this.synchronize()
    }, delayMs)
  }

  async synchronize() {
    const authStore = useAuthStore()
    if (
      !authStore.isAuthenticated ||
      !connectivityService.isEffectivelyOnline.value ||
      !this.hasWindowFocus()
    ) {
      return
    }

    if (this.isSyncing) {
      this.syncRequested = true
      return
    }

    this.isSyncing = true
    this.syncRequested = false

    try {
      const dirtySettings = settingsService.getDirtySyncableSettings()
      const syncedKeys = new Set<string>()
      const syncedUpdatedAtByKey = new Map<string, number>()

      let remoteSettings: SyncableUiSetting[]
      if (dirtySettings.length > 0) {
        dirtySettings.forEach((setting) => {
          syncedKeys.add(setting.key)
          syncedUpdatedAtByKey.set(setting.key, setting.updatedAt)
        })

        const response = await apiService.syncUiSettings(
          dirtySettings.map((setting) => ({ key: setting.key, value: setting.value })),
        )
        remoteSettings = response.settings.map(toSyncableSetting)
      } else {
        const response = await apiService.getUiSettings()
        remoteSettings = response.settings.map(toSyncableSetting)
      }

      settingsService.applyRemoteSettings(remoteSettings, {
        syncedKeys,
        syncedUpdatedAtByKey,
      })
    } catch (error) {
      logService.warn(LOG_CATEGORIES.API, 'Failed to sync UI settings', { error })
    } finally {
      this.isSyncing = false
      if (this.syncRequested) {
        this.scheduleSync(0)
      }
    }
  }

  private clearChangeTimer() {
    if (this.changeTimer === null) return
    clearTimeout(this.changeTimer)
    this.changeTimer = null
  }
}

export const uiSettingsSyncService = new UiSettingsSyncService()
